import React, { useState } from 'react';
import { 
  Box, 
  Typography, 
  Tabs, 
  Tab, 
  Grid, 
  Paper, 
  Divider
} from '@mui/material';
import ChartEditor from './ChartEditor';
import ChartViewer from './ChartViewer';
import ColorThumbnail from './ColorThumbnail';
import ColorThumbnailControls from './ColorThumbnailControls';

// Sample chart data for demonstration
const sampleChartData = {
  knitdata: [
    ['k', 'k', 'p', 'p', 'k', 'k', 'p', 'p'],
    ['p', 'p', 'k', 'k', 'p', 'p', 'k', 'k'],
    ['k', 'k2tog', 'yo', 'k', 'k', 'k2tog', 'yo', 'k'],
    ['p', 'p', 'p', 'p', 'p', 'p', 'p', 'p'],
    ['k', 'yo', 'ssk', 'k', 'k', 'yo', 'ssk', 'k'],
    ['p', 'p', 'k', 'k', 'p', 'p', 'k', 'k'],
    ['k', 'k', 'p', 'p', 'k', 'k', 'p', 'p'],
    ['p', 'p', 'p', 'p', 'p', 'p', 'p', 'p']
  ],
  colordata: [
    [0, 0, 1, 1, 0, 0, 1, 1],
    [1, 1, 0, 0, 1, 1, 0, 0],
    [0, 0, 0, 0, 0, 0, 0, 0],
    [1, 1, 1, 1, 1, 1, 1, 1],
    [0, 0, 0, 0, 0, 0, 0, 0],
    [1, 1, 0, 0, 1, 1, 0, 0],
    [0, 0, 1, 1, 0, 0, 1, 1],
    [1, 1, 1, 1, 1, 1, 1, 1]
  ],
  colors: [
    '#ffffff', // 0: white
    '#e3f2fd', // 1: light blue
    '#bbdefb', // 2: medium blue
    '#64b5f6', // 3: blue
    '#2196f3', // 4: dark blue
  ],
  icons: ['', '', '', '', ''],
  verticalLines: [2, 6],
  title: "Sample Lace Pattern"
};

function KnitChartPlayground() {
  const [activeTab, setActiveTab] = useState(0);
  const [chartData, setChartData] = useState(sampleChartData);
  
  // Thumbnail gallery controls
  const [thumbnailControls, setThumbnailControls] = useState({
    width: 120,
    height: 90,
    view: 'watercolor',
    cellRatio: '4:3',
    showGrid: false // NEW: gridlines
  });

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleChartExport = (exportData) => {
    console.log('Exporting chart data:', exportData);
    // Custom export logic can be implemented here
  };

  return (
    <Box sx={{ p: 3, maxWidth: '1400px', margin: '0 auto' }}>
      <Typography variant="h4" component="h1" gutterBottom sx={{ mb: 3 }}>
        Chart Playground
      </Typography>
      
      <Typography variant="body1" sx={{ mb: 3, color: 'text.secondary' }}>
        Explore the knitting chart tools. Use the Chart Viewer to see sample patterns or switch to the Chart Editor to create your own.
      </Typography>

      <Tabs value={activeTab} onChange={handleTabChange} sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}>
        <Tab label="Chart Viewer Demo" />
        <Tab label="Chart Editor" />
        <Tab label="Thumbnail Gallery" />
      </Tabs>

      {activeTab === 0 && (
        <Box>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Chart Viewer Demo
          </Typography>
          <Typography variant="body2" sx={{ mb: 3, color: 'text.secondary' }}>
            This demonstrates the ChartViewer component displaying a sample knitting chart with lace pattern elements.
          </Typography>
          
          <ChartViewer
            knitdata={chartData.knitdata}
            colordata={chartData.colordata}
            colors={chartData.colors}
            icons={chartData.icons}
            verticalLines={chartData.verticalLines}
            title={chartData.title}
            onExport={handleChartExport}
            defaultCellSize={28}
          />
        </Box>
      )}

      {activeTab === 1 && (
        <Box>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Chart Editor
          </Typography>
          <Typography variant="body2" sx={{ mb: 3, color: 'text.secondary' }}>
            Create and edit knitting charts. Changes made here will be reflected in the Chart Viewer tab and the Thumbnail Gallery.
          </Typography>
          <Box sx={{ border: '1px solid #ddd', borderRadius: 1, p: 2 }}>
            <ChartEditor
              knitdata={chartData.knitdata}
              colordata={chartData.colordata}
              colors={chartData.colors}
              icons={chartData.icons}
              verticalLines={chartData.verticalLines}
              onSave={newChartData => {
                setChartData(newChartData);
                // Optionally, you can also reset the tab to the gallery or viewer if desired
              }}
              onCancel={() => console.log('Edit cancelled')}
            />
          </Box>
        </Box>
      )}

      {activeTab === 2 && (
        <Box>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Chart Thumbnail Gallery
          </Typography>
          <Typography variant="body2" sx={{ mb: 3, color: 'text.secondary' }}>
            Preview how your chart data looks as thumbnails with different visual styles and sizing options. 
            The thumbnails use the same chart data from the Chart Editor tab.
          </Typography>
          
          <Grid container spacing={3}>
            {/* Interactive Controls */}
            <Grid item xs={12} md={8}>
              <ColorThumbnailControls
                colordata={chartData.colordata}
                colors={chartData.colors}
                thumbnailWidth={thumbnailControls.width}
                thumbnailHeight={thumbnailControls.height}
                thumbnailView={thumbnailControls.view}
                thumbnailCellRatio={thumbnailControls.cellRatio}
                thumbnailShowGrid={thumbnailControls.showGrid}
                onWidthChange={value => setThumbnailControls(prev => ({ ...prev, width: value }))}
                onHeightChange={value => setThumbnailControls(prev => ({ ...prev, height: value }))}
                onViewChange={value => setThumbnailControls(prev => ({ ...prev, view: value }))}
                onCellRatioChange={value => setThumbnailControls(prev => ({ ...prev, cellRatio: value }))}
                onShowGridChange={checked => setThumbnailControls(prev => ({ ...prev, showGrid: checked }))}
                title="Chart Data Preview"
                layout="horizontal"
                settings={{}}
              />
            </Grid>

            {/* Chart Data Info Panel */}
            <Grid item xs={12} md={4}>
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Chart Data Info
                </Typography>
                
                <Typography variant="body2" sx={{ mb: 2 }}>
                  The thumbnail gallery now uses the same chart data as the Chart Editor. 
                  Any changes made in the editor will be reflected here.
                </Typography>

                <Divider sx={{ my: 3 }} />

                {/* Chart Information */}
                <Typography variant="subtitle2" gutterBottom>
                  Chart Details
                </Typography>
                <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem', mb: 2 }}>
                  Size: {chartData.colordata?.length || 0}×{chartData.colordata?.[0]?.length || 0} cells<br/>
                  Colors: {chartData.colors?.length || 0} in palette<br/>
                  Pattern: {chartData.title || 'Sample Pattern'}
                </Typography>

                <Divider sx={{ my: 3 }} />

                {/* Current Thumbnail Settings */}
                <Typography variant="subtitle2" gutterBottom>
                  Thumbnail Settings
                </Typography>
                <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                  Size: {thumbnailControls.width}×{thumbnailControls.height}px<br/>
                  View: {thumbnailControls.view}<br/>
                  Ratio: {thumbnailControls.cellRatio}
                </Typography>

                <Box sx={{ mt: 3, p: 2, bgcolor: 'info.light', borderRadius: 1 }}>
                  <Typography variant="caption" sx={{ color: 'info.contrastText' }}>
                    💡 Tip: Switch to the Chart Editor tab to modify the pattern, 
                    then return here to see the updated thumbnails.
                  </Typography>
                </Box>
              </Paper>
            </Grid>
          </Grid>
          
          {/* Sample Gallery - Static Examples */}
          <Grid container spacing={3} sx={{ mt: 2 }}>
            {/* Size Comparison */}
            <Grid item xs={12}>
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Size Comparison
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', justifyContent: 'center' }}>
                  {[
                    { size: 60, label: 'Small' },
                    { size: 90, label: 'Medium' },
                    { size: 120, label: 'Large' },
                    { size: 150, label: 'XL' }
                  ].map(({ size, label }) => (
                    <Box key={size} sx={{ textAlign: 'center' }}>
                      <Typography variant="caption" display="block" gutterBottom>
                        {label} ({size}px)
                      </Typography>
                      <ColorThumbnail
                        colordata={chartData.colordata}
                        colors={chartData.colors}
                        width={size}
                        height={size * 0.75}
                        view={thumbnailControls.view}
                        cellRatio={thumbnailControls.cellRatio}
                        showGrid={thumbnailControls.showGrid}
                        settings={{}}
                      />
                    </Box>
                  ))}
                </Box>
              </Paper>
            </Grid>

            {/* View Styles Comparison */}
            <Grid item xs={12}>
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>
                  View Styles Comparison
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', justifyContent: 'center' }}>
                  {['default', 'watercolor', 'soft', 'minimal'].map((viewStyle) => (
                    <Box key={viewStyle} sx={{ textAlign: 'center' }}>
                      <Typography variant="caption" display="block" gutterBottom>
                        {viewStyle}
                      </Typography>
                      <ColorThumbnail
                        colordata={chartData.colordata}
                        colors={chartData.colors}
                        width={100}
                        height={75}
                        view={viewStyle}
                        cellRatio={thumbnailControls.cellRatio}
                        showGrid={thumbnailControls.showGrid}
                        settings={{}}
                      />
                    </Box>
                  ))}
                </Box>
              </Paper>
            </Grid>

            {/* Cell Ratios Comparison */}
            <Grid item xs={12}>
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Cell Ratios Comparison
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', justifyContent: 'center' }}>
                  {[
                    { ratio: '1:1', label: 'Square' },
                    { ratio: '3:4', label: 'Portrait' },
                    { ratio: '4:3', label: 'Landscape' },
                    { ratio: '2:3', label: 'Tall' },
                    { ratio: '3:2', label: 'Wide' }
                  ].map(({ ratio, label }) => (
                    <Box key={ratio} sx={{ textAlign: 'center' }}>
                      <Typography variant="caption" display="block" gutterBottom>
                        {label} ({ratio})
                      </Typography>
                      <ColorThumbnail
                        colordata={chartData.colordata}
                        colors={chartData.colors}
                        width={100}
                        height={75}
                        view={thumbnailControls.view}
                        cellRatio={ratio}
                        showGrid={thumbnailControls.showGrid}
                        settings={{}}
                      />
                    </Box>
                  ))}
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </Box>
      )}
    </Box>
  );
}

export default KnitChartPlayground;
